import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CommitIcon,
  AppRegistration as RegisterIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { analysisFindingsAPI } from '../../services/api';
import { getSampleColumns, renderSampleRow } from './sampleFindings';

/**
 * Minimal upload UI for analysis findings (Phase 2 floor — see plan doc).
 *
 * Flow:
 *   1. Pick a (module, parser) combination from /pipelines. The selection
 *      reveals one file picker per declared file_key plus an optional
 *      notes field.
 *   2. As soon as all required files are attached, the page auto-fires
 *      POST /preview (dry-run) and renders issues, specimen-mapping
 *      summary, finding count, and a sample_findings table inline. File
 *      changes re-trigger preview automatically.
 *   3. Commit button is enabled only when the latest preview is for the
 *      current file set AND returned ok:true. Clicking it → POST /commit;
 *      shows the resulting run_id.
 *
 * No deep-link affordances from project/specimen pages — those come if/when
 * we polish.
 */

// Re-ingest notices (#56): how this upload relates to prior runs of the same
// pipeline. Shared by the preview (mode="preview", future tense) and the
// commit result (mode="committed", past tense). Renders nothing when the
// upload is an ordinary new run with no dedup / supersession / overlap.
const ReingestNotices = ({ result, mode }) => {
  if (!result) return null;
  const dup = result.duplicate_of;
  const superseded = result.superseded_runs || [];
  const overlaps = result.overlap_warnings || [];
  if (!dup && superseded.length === 0 && overlaps.length === 0) return null;

  const done = mode === 'committed';
  const plural = (n) => (n === 1 ? '' : 's');

  return (
    <Box sx={{ mb: 2 }}>
      {dup && (
        <Alert severity="info" sx={{ mb: 0.5 }}>
          {done
            ? `Identical to run #${dup.run_number} — nothing was ingested, so no duplicate was created.`
            : `Identical to run #${dup.run_number} — submitting will not ingest anything.`}
        </Alert>
      )}
      {superseded.length > 0 && (
        <Alert severity="info" sx={{ mb: 0.5 }}>
          {done ? 'Superseded ' : 'Will supersede '}
          {superseded
            .map((s) => `run #${s.run_number} (${s.specimen_count} specimen${plural(s.specimen_count)})`)
            .join(', ')}
          {' '}— those results are hidden from findings views; the run stays in history.
        </Alert>
      )}
      {overlaps.map((o, i) => (
        <Alert key={i} severity="warning" sx={{ mb: 0.5 }}>
          Partial overlap with run #{o.run_number}: shares {o.shared_specimens} specimen
          {plural(o.shared_specimens)}, but that run also covers {o.run_only_specimens} other
          {plural(o.run_only_specimens)}. Both runs stay active, so the shared specimen
          {plural(o.shared_specimens)} may be counted twice in findings views — review before relying on counts.
        </Alert>
      ))}
    </Box>
  );
};

const AnalysisUpload = () => {
  const [pipelines, setPipelines] = useState([]);
  const [loadingPipelines, setLoadingPipelines] = useState(true);
  const [pipelinesError, setPipelinesError] = useState(null);

  const [moduleName, setModuleName] = useState('');
  const [parserName, setParserName] = useState('');

  // file_key → File object (or null)
  const [files, setFiles] = useState({});
  const [notes, setNotes] = useState('');

  const [committing, setCommitting] = useState(false);
  // Preview is fired automatically when (parser + required files) changes.
  // `signature` is the file-set fingerprint the result is bound to; Commit
  // requires preview.signature === currentSignature to prevent committing a
  // different file set than was previewed.
  const [preview, setPreview] = useState({
    status: 'idle', // 'idle' | 'previewing' | 'ok' | 'issues' | 'error'
    signature: null,
    result: null,
    error: null,
  });
  const [commitResult, setCommitResult] = useState(null);
  // When the preview reports unmatched rows, Submit opens this confirmation
  // dialog first (listing what will be skipped) rather than uploading directly.
  const [confirmSkipOpen, setConfirmSkipOpen] = useState(false);

  // Request counter for discarding stale preview responses if signatures
  // change while a request is in flight.
  const previewRequestRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    analysisFindingsAPI
      .getPipelines()
      .then((res) => {
        if (cancelled) return;
        setPipelines(res.data.pipelines || []);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.response?.data?.msg || err.message || 'Failed to load pipelines';
        setPipelinesError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoadingPipelines(false);
      });
    return () => { cancelled = true; };
  }, []);

  const moduleNames = useMemo(
    () => Array.from(new Set(pipelines.map((p) => p.module_name).filter(Boolean))).sort(),
    [pipelines]
  );

  const parsersForModule = useMemo(
    () => pipelines.filter((p) => p.module_name === moduleName),
    [pipelines, moduleName]
  );

  const selectedParser = useMemo(
    () => pipelines.find((p) => p.name === parserName && p.module_name === moduleName) || null,
    [pipelines, moduleName, parserName]
  );

  // When the parser changes, reset files + any previous preview/commit.
  // Notes intentionally do NOT reset preview — they don't affect validate/parse.
  useEffect(() => {
    setFiles({});
    setPreview({ status: 'idle', signature: null, result: null, error: null });
    setCommitResult(null);
  }, [parserName, moduleName]);

  // Stable signature of "what would be parsed" so Commit can require that
  // the latest preview matches the current file set. Notes are excluded —
  // they're metadata on the run, not an input to validate/parse, so changing
  // notes doesn't invalidate a preview.
  const currentSignature = useMemo(() => {
    if (!selectedParser) return null;
    const keys = selectedParser.files.map((f) => f.key).sort();
    return JSON.stringify({
      module: moduleName,
      parser: parserName,
      files: keys.map((k) => {
        const f = files[k];
        return f ? `${k}:${f.name}:${f.size}:${f.lastModified}` : `${k}:null`;
      }),
    });
  }, [selectedParser, moduleName, parserName, files]);

  const requiredFilesPresent = useMemo(() => {
    if (!selectedParser) return false;
    return selectedParser.files
      .filter((f) => f.required !== false)
      .every((f) => files[f.key] instanceof File);
  }, [selectedParser, files]);

  const sampleColumns = useMemo(
    () => getSampleColumns(selectedParser?.output?.kind),
    [selectedParser]
  );

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('module', moduleName);
    fd.append('parser', parserName);
    if (notes) fd.append('notes', notes);
    for (const fileKey of Object.keys(files)) {
      const f = files[fileKey];
      if (f) fd.append(fileKey, f, f.name);
    }
    return fd;
  };

  // Auto-fire preview whenever (parser + required files) is satisfied and
  // the current file-set signature differs from the last previewed one.
  // Stale responses are discarded by request-id check.
  useEffect(() => {
    if (!selectedParser || !requiredFilesPresent) return;
    if (preview.signature === currentSignature) return;
    if (committing) return;

    const reqId = ++previewRequestRef.current;
    setPreview((s) => ({ ...s, status: 'previewing', error: null }));
    // commitResult clears the moment the file set changes from what was
    // committed, so the success alert doesn't linger over a fresh upload.
    setCommitResult(null);

    analysisFindingsAPI
      .preview(buildFormData())
      .then((res) => {
        if (previewRequestRef.current !== reqId) return; // stale
        setPreview({
          status: res.data.ok ? 'ok' : 'issues',
          signature: currentSignature,
          result: res.data,
          error: null,
        });
      })
      .catch((err) => {
        if (previewRequestRef.current !== reqId) return; // stale
        const status = err.response?.status;
        const data = err.response?.data;
        // Server returns the same shape on 422 / 404 — surface it inline.
        if (data && (data.issues || data.specimen_mapping)) {
          setPreview({
            status: 'issues',
            signature: currentSignature,
            result: data,
            error: data.msg || `Preview failed (HTTP ${status})`,
          });
        } else {
          setPreview({
            status: 'error',
            signature: currentSignature,
            result: null,
            error: data?.msg || err.message || 'Preview failed',
          });
        }
      });
    // buildFormData isn't a dep — it's just a closure over current state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSignature, selectedParser, requiredFilesPresent, committing]);

  const unmatched = preview.result?.specimen_mapping?.unmatched || [];

  // Submit button → if the preview flagged rows that don't match a LIMS
  // specimen, make the user confirm skipping them before we upload. A clean
  // (all-matched) upload skips the dialog and commits directly.
  const handleSubmitClick = () => {
    if (unmatched.length > 0) {
      setConfirmSkipOpen(true);
    } else {
      doCommit();
    }
  };

  const doCommit = async () => {
    setConfirmSkipOpen(false);
    setCommitting(true);
    try {
      const res = await analysisFindingsAPI.commit(buildFormData());
      setCommitResult(res.data);
      if (res.data.committed) {
        toast.success(`Submitted — run #${res.data.run_number}`);
      } else if (res.data.duplicate_of) {
        toast.info(`Identical to run #${res.data.duplicate_of.run_number} — nothing ingested`);
      } else {
        toast.info('Nothing ingested');
      }
    } catch (err) {
      const data = err.response?.data;
      toast.error(data?.msg || err.message || 'Submit failed');
    } finally {
      setCommitting(false);
    }
  };

  const previewing = preview.status === 'previewing';
  const canCommit =
    preview.status === 'ok' &&
    preview.signature === currentSignature &&
    !previewing &&
    !committing &&
    commitResult == null;

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <UploadIcon color="primary" />
        <Typography variant="h5">Analysis Upload</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          component={RouterLink}
          to="/analysis/pipelines/register"
          variant="outlined"
          size="small"
          startIcon={<RegisterIcon />}
        >
          Register a new pipeline
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Pick a module and pipeline, attach the file(s) it declares, run a preview, then submit.
        Preview never writes; submit is gated on a clean preview of the same files.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
        {loadingPipelines ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading pipelines…</Typography>
          </Box>
        ) : pipelinesError ? (
          <Alert severity="error">Failed to load pipelines: {pipelinesError}</Alert>
        ) : pipelines.length === 0 ? (
          <Alert
            severity="info"
            action={
              <Button
                component={RouterLink}
                to="/analysis/pipelines/register"
                color="inherit"
                size="small"
                startIcon={<RegisterIcon />}
              >
                Register one now
              </Button>
            }
          >
            No pipelines registered yet. Register one to get started.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Module</InputLabel>
                <Select
                  value={moduleName}
                  label="Module"
                  onChange={(e) => { setModuleName(e.target.value); setParserName(''); }}
                >
                  {moduleNames.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={!moduleName}>
                <InputLabel>Parser</InputLabel>
                <Select
                  value={parserName}
                  label="Parser"
                  onChange={(e) => setParserName(e.target.value)}
                >
                  {parsersForModule.map((p) => (
                    <MenuItem key={p.name} value={p.name}>
                      {p.display_name} ({p.name} @ {p.version})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {selectedParser && (
              <>
                {selectedParser.description && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedParser.description}
                  </Typography>
                )}

                <Divider />

                <Typography variant="subtitle2">Files</Typography>
                <Stack spacing={1.5}>
                  {selectedParser.files.map((spec) => (
                    <Box key={spec.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ minWidth: 200 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {spec.key}
                          {spec.required !== false && (
                            <Chip label="required" size="small" sx={{ ml: 1, height: 18 }} />
                          )}
                        </Typography>
                        {spec.description && (
                          <Typography variant="caption" color="text.secondary">
                            {spec.description}
                          </Typography>
                        )}
                      </Box>
                      <Button variant="outlined" component="label" size="small">
                        {files[spec.key] ? files[spec.key].name : 'Choose file'}
                        <input
                          type="file"
                          hidden
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            setFiles((s) => ({ ...s, [spec.key]: f }));
                          }}
                        />
                      </Button>
                      {files[spec.key] && (
                        <Typography variant="caption" color="text.secondary">
                          {(files[spec.key].size / 1024).toFixed(1)} KB
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>

                <TextField
                  label="Notes (optional)"
                  size="small"
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  helperText="Free-text note attached to the run record."
                />

                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={committing ? <CircularProgress size={16} /> : <CommitIcon />}
                    onClick={handleSubmitClick}
                    disabled={!canCommit}
                  >
                    {committing ? 'Submitting…' : 'Submit'}
                  </Button>
                  {previewing && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Previewing…
                      </Typography>
                    </Box>
                  )}
                  {!previewing && !requiredFilesPresent && selectedParser && (
                    <Typography variant="caption" color="text.secondary">
                      Attach all required files to preview.
                    </Typography>
                  )}
                  {!previewing && preview.status === 'error' && (
                    <Typography variant="caption" color="error">
                      Preview failed: {preview.error}
                    </Typography>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Paper>

      {preview.result && (
        <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
          <Typography variant="h6" gutterBottom>Preview</Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Chip
              label={preview.result.ok ? 'OK' : 'Issues'}
              color={preview.result.ok ? 'success' : 'warning'}
              size="small"
            />
            <Chip
              label={`Specimens: ${preview.result.specimen_mapping?.matched ?? 0} matched, ${preview.result.specimen_mapping?.unmatched?.length ?? 0} unmatched`}
              size="small"
            />
            <Chip label={`Findings: ${preview.result.finding_count ?? 0}`} size="small" />
            {previewing && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">refreshing…</Typography>
              </Box>
            )}
          </Stack>

          {preview.result.issues && preview.result.issues.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {preview.result.issues.map((iss, i) => (
                <Alert
                  key={i}
                  severity={iss.level === 'error' ? 'error' : 'warning'}
                  sx={{ mb: 0.5 }}
                >
                  {iss.message}
                  {iss.row != null && (
                    <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                      (row {iss.row})
                    </Typography>
                  )}
                </Alert>
              ))}
            </Box>
          )}

          {preview.result.specimen_mapping?.unmatched?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {preview.result.specimen_mapping.unmatched.length} row(s) don't match a LIMS
                specimen and will be skipped:
              </Typography>
              <Typography variant="caption">
                {preview.result.specimen_mapping.unmatched.join(', ')}
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                These are usually controls (water/negative). Submit will ask you to confirm
                before skipping them — check the list for any mistyped specimen numbers.
              </Typography>
            </Alert>
          )}

          <ReingestNotices result={preview.result} mode="preview" />

          {preview.result.sample_findings && preview.result.sample_findings.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sample findings (first {preview.result.sample_findings.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>WUID</TableCell>
                      {sampleColumns.map((c) => (
                        <TableCell key={c}>{c}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.result.sample_findings.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell>{f.wuid}</TableCell>
                        {renderSampleRow(f, selectedParser?.output?.kind).map((v, j) => (
                          <TableCell key={j}>{v}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {commitResult && (
        <Paper sx={{ p: 3 }} elevation={1}>
          {commitResult.committed ? (
            <Alert severity="success">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Submitted run #{commitResult.run_number}
              </Typography>
              <Typography variant="caption">
                {commitResult.finding_count} findings written across{' '}
                {commitResult.specimen_mapping?.matched ?? 0} specimens.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Nothing ingested
              </Typography>
              <Typography variant="caption">
                {commitResult.duplicate_of
                  ? `This upload is identical to run #${commitResult.duplicate_of.run_number}; no new run was created.`
                  : 'No new run was created.'}
              </Typography>
            </Alert>
          )}
          <ReingestNotices result={commitResult} mode="committed" />
        </Paper>
      )}

      <Dialog open={confirmSkipOpen} onClose={() => setConfirmSkipOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {unmatched.length} row{unmatched.length === 1 ? '' : 's'} will be skipped
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            These specimen number{unmatched.length === 1 ? '' : 's'} aren't in the LIMS:
            <Box
              component="div"
              sx={{ my: 1.5, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-word' }}
            >
              {unmatched.join(', ')}
            </Box>
            They're usually controls (water/negative) — but if any of these is a mistyped real
            specimen, cancel and fix it first. Otherwise, upload the remaining{' '}
            {preview.result?.specimen_mapping?.matched ?? 0} specimen
            {(preview.result?.specimen_mapping?.matched ?? 0) === 1 ? '' : 's'} and skip these?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSkipOpen(false)}>Cancel</Button>
          <Button onClick={doCommit} variant="contained" color="primary" startIcon={<CommitIcon />}>
            Skip &amp; upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnalysisUpload;
